import type Opensearch from '@opensearch-project/opensearch';
import type { ErrorReporter } from '../../common/ErrorReporter';
import { sharedStateHasSearchableProperties, type SharedStates } from '../../ReplicantSharedStates';
import { mapObject } from '../../utils/ObjUtils';
import type { SharedStateIndexDBs } from '../IndexDB';
import createIndexDBElasticSearch from './IndexDBElasticSearch';
import createIndexDBLocalStorage from './IndexDBInLocalStorage';
import createIndexDBInMem from './IndexDBInMem';

export function createSharedStateIndexDBs<TSharedStates extends SharedStates>(opts: {
    dateNow?: () => number;
    errorReporter?: ErrorReporter;
    immediateRefresh?: boolean;
    indexName: string;
    migrateToOpensearchClient?: Opensearch.Client;
    opensearchClient: Opensearch.Client;
    sharedStates: TSharedStates;
}): SharedStateIndexDBs<TSharedStates> {
    return mapObject(getSharedStatesWithComputedProperties(opts.sharedStates), (sharedStateName, sharedState) =>
        createIndexDBElasticSearch(
            opts.opensearchClient,
            opts.indexName + '_' + sharedStateName.toLowerCase(),
            sharedState.computedProperties,
            {
                dateNow: opts.dateNow,
                immediateRefresh: opts.immediateRefresh,
                errorReporter: opts.errorReporter,
                migrateToOpensearchClient: opts.migrateToOpensearchClient,
            },
        ),
    );
}

export function createSharedStateIndexDBsLocalStorage<TSharedStates extends SharedStates>(opts: {
    indexName: string;
    prefix?: string;
    sharedStates: TSharedStates;
    storageOverride?: Storage;
}): SharedStateIndexDBs<TSharedStates> {
    return mapObject(getSharedStatesWithComputedProperties(opts.sharedStates), (sharedStateName, sharedState) =>
        createIndexDBLocalStorage(opts.indexName + sharedStateName, sharedState.computedProperties, {
            storage: opts.storageOverride,
            prefix: opts.prefix,
        }),
    );
}

export function createSharedStateIndexDBsInMem<TSharedStates extends SharedStates>(opts: {
    indexName: string;
    sharedStates: TSharedStates;
}): SharedStateIndexDBs<TSharedStates> {
    return mapObject(getSharedStatesWithComputedProperties(opts.sharedStates), (sharedStateName, sharedState) =>
        createIndexDBInMem(opts.indexName + sharedStateName, sharedState.computedProperties),
    );
}

function getSharedStatesWithComputedProperties<TSharedStates extends SharedStates>(
    sharedStates: TSharedStates,
): TSharedStates {
    return Object.entries(sharedStates).reduce((acc, [stateName, sharedState]) => {
        if (!sharedStateHasSearchableProperties(sharedState)) {
            return acc;
        }

        return { ...acc, [stateName]: sharedState };
    }, {} as TSharedStates);
}
